/**
 * Simplified & polished effect presets for Idle Neon Miner
 * — единая неоновая палитра
 * — аккуратные дефолт-физики
 * — минимум слоёв (понятнее, дешевле)
 * — регулятор FX_QUALITY для масштабирования нагрузки
 */

import type { ParticleProperties } from './ParticleProperties';
import { Easing } from './ParticleTypes';

// Глобальный скейл качества/нагрузки: 0.5 = low, 1 = medium, 1.5..2 = high
const FX_QUALITY = 2;
const Q = (n: number) => Math.max(1, Math.round(n * FX_QUALITY));

// Единая неоновая палитра + тёмный «фоновый дым»
const PALETTE = {
  neon: [0xff3af3, 0x41f7ff, 0xfff75a, 0xff7a2f],
  cyan: 0x41f7ff,
  cyanDeep: 0x0055ff,
  fade: 0x001020, // мягкий холодный тёмный
};

// Аккуратные дефолтные параметры, которые держат общий вайб
const DEF = {
  friction: 0.9,
  rotSlow: [-20, 20] as [number, number],
  rotFast: [-45, 45] as [number, number],
  easeOut: Easing.outCubic,
  easeOutSoft: Easing.outSine,
  easeIn: Easing.inQuad,
  easeLin: Easing.linear,
};

// ----------------------------
// PRESETS
// ----------------------------
export const effectPresets = {
  /**
   * ВЗРЫВ — мощный двухслойный эффект с неоновым циановым взрывом!
   * Быстрые квадраты для энергии + мягкое свечение для объема. Максимально зрелищно!
   */
  explosion: {
    particles: [
      // Основной взрыв - быстрые квадраты с высокой скоростью
      {
        properties: {
          texture: 'bordered_square',
          count: Q(20),
          speed: { start: [400, 800], end: 0, ease: DEF.easeOut },
          scale: { start: [0.4, 1.0], end: 0.02, ease: DEF.easeIn },
          color: { start: 0xffffff, end: 0xffffff, ease: DEF.easeLin },
          alpha: { start: 1.0, end: 0, ease: DEF.easeIn },
          acceleration: { x: 0, y: 0 },
          lifetime: [0.6, 1.0],
          angle: [0, Math.PI * 2],
          friction: 0.75,
          rotationSpeed: [-90, 90],
        } satisfies ParticleProperties,
      },
      // Внутреннее свечение - медленные мягкие частицы для объема
      {
        properties: {
          texture: 'soft_circle',
          count: Q(30),
          speed: { start: [150, 350], end: 0, ease: DEF.easeOutSoft },
          scale: { start: [0.8, 1.5], end: 0.1, ease: DEF.easeOutSoft },
          color: { start: 0x7af7ff, end: PALETTE.fade, ease: DEF.easeIn },
          alpha: { start: 0.7, end: 0, ease: DEF.easeOutSoft },
          acceleration: { x: 0, y: 0 },
          lifetime: [0.8, 1.4],
          angle: [0, Math.PI * 2],
          friction: 0.85,
          rotationSpeed: 0,
        } satisfies ParticleProperties,
      },
    ],
    sound: 'explosion_large',
    screenShake: { intensity: 0, duration: 0.5 },
  },

  /**
   * СЛЕД — лёгкое свечение без лишних параметров.
   * Один слой, плавное дыхание, длинная жизнь.
   */
  trail: {
    particles: [
      {
        properties: {
          texture: 'soft_circle',
          count: 1,
          speed: { start: 0, end: 0, ease: DEF.easeLin },
          scale: { start: [5, 10], end: [1, 2], ease: DEF.easeOutSoft },
          color: { start: PALETTE.cyan, end: PALETTE.cyanDeep, ease: DEF.easeLin },
          alpha: { start: 0.55, end: 0, ease: DEF.easeOutSoft },
          acceleration: { x: 0, y: 0 },
          friction: 0.985,
          lifetime: [1.4, 2.6],
          angle: [0, 0],
          rotationSpeed: 0,
        } satisfies ParticleProperties,
      },
    ],
  },

  /**
   * МАЙНИНГ — направленные искры + лёгкая пыль.
   * Чёткая читаемость удара резаком, экономный по вычислениям.
   */
  mining: {
    particles: [
      // Основные искры (квадраты) — направленные вперёд-вниз
      {
        properties: {
          texture: 'soft_circle',
          count: Q(8),
          speed: { start: [80, 140], end: 0, ease: DEF.easeOut },
          scale: { start: [0.4, 0.8], end: 0.05, ease: DEF.easeIn },
          color: {
            start: [PALETTE.cyan, 0x7af7ff, 0xfff75a],
            end: PALETTE.fade,
            ease: DEF.easeLin,
          },
          alpha: { start: 0.8, end: 0, ease: DEF.easeIn },
          acceleration: { x: 0, y: 40 },
          lifetime: [0.3, 0.6],
          angle: [-Math.PI / 6, Math.PI / 6], // узкий конус вперёд
          friction: DEF.friction,
          rotationSpeed: [-15, 15],
          oscillation: { frequency: [2, 4], amplitude: [1, 3] },
        } satisfies ParticleProperties,
      },
      // Пыль/глоу (круги) — заполняет пространство, подчёркивает направление
      {
        properties: {
          texture: 'soft_circle',
          count: Q(4),
          speed: { start: [30, 60], end: [5, 15], ease: DEF.easeOutSoft },
          scale: { start: [0.3, 0.6], end: [0.1, 0.3], ease: DEF.easeOutSoft },
          color: { start: [PALETTE.cyan, 0x7af7ff], end: PALETTE.fade, ease: DEF.easeLin },
          alpha: { start: 0.5, end: 0, ease: DEF.easeOutSoft },
          acceleration: { x: 0, y: 25 },
          lifetime: [0.4, 0.8],
          angle: [-Math.PI / 4, Math.PI / 4],
          friction: 0.96,
          rotationSpeed: 0,
        } satisfies ParticleProperties,
      },
    ],
    sound: 'mining_medium_loop',
  },
} as const;

// Типы остаются теми же
export interface EffectPreset {
  particles: Array<{ properties: ParticleProperties }>;
  sound?: string;
  screenShake?: { intensity: number; duration: number };
}

export type EffectPresetName = keyof typeof effectPresets;
